import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { PaginationInputProps } from "@core/domain/models/pagination.model.ts";
import { PaginationSchema } from "@core/domain/models/pagination.model.ts";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { StationReportSummarySchema } from "@report/domain/models/report-summary.model.ts";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

export const ReportCustomTypeEnum = {
    MaxValues: "maxValues",
    MinValues: "minValues",
    AvgValues: "avgValues",
    SumValues: "sumValues",
    Measures: "measures",
    TotalSum: "totalSum",
    Tendency: "tendency",
} as const;
export type ReportCustomTypeEnum =
    (typeof ReportCustomTypeEnum)[keyof typeof ReportCustomTypeEnum];
const ReportCustomTypeEnumSchema = z.enum(ReportCustomTypeEnum);

export const isReportCustomType = (
    value: unknown,
): value is ReportCustomTypeEnum => ReportCustomTypeEnumSchema.validate(value);

const RecordValuesSchema = z.record(
    z.string(),
    z.nullable(z.union([z.number(), z.string().trim()])),
);

const ReportSchema = z
    .object({
        stationReportSummary: StationReportSummarySchema,
        items: z.array(RecordValuesSchema),
        pagination: PaginationSchema,
        stats: z
            .array(z.record(z.string(), z.string().trim().nullable()))
            .optional(),
    })
    .brand<"Report">();

export type ReportInputProps = z.input<typeof ReportSchema>;
export type ReportProps = z.output<typeof ReportSchema>;

export class Report {
    private constructor(private readonly props: Readonly<ReportProps>) {}

    get stationReportSummary(): Readonly<
        Unbrand<ReportProps>["stationReportSummary"]
    > {
        return this.props.stationReportSummary;
    }

    get items(): Record<string, string | number | null>[] {
        return this.props.items;
    }

    get pagination(): PaginationInputProps {
        return this.props.pagination;
    }

    get stats(): ReportProps["stats"] {
        return this.props.stats;
    }

    static validate(
        props: Readonly<MaybeValidProps<ReportInputProps>>,
    ): Result<ReportProps, z.ZodError<ReportProps>> {
        const result = ReportSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<ReportInputProps>>,
    ): Result<ReportProps, PartialValid<typeof Report>> {
        const result = partialSafeParse(ReportSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<ReportInputProps>,
    ): Result<Report, z.ZodError<ReportProps>> {
        return Report.validate(props).map(
            (validatedProps) => new Report(validatedProps),
        );
    }

    static createWithRecovery(
        props: ReportInputProps,
    ): Result<Report, ModelError<typeof Report>> {
        return Report.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.stationReportSummary?.stationId)
                    return new NonRecuperableModelError();

                const defaultProps: Unbrand<ReportProps> = {
                    stationReportSummary: {
                        stationId: 0,
                        stationName: "",
                        icon: "",
                    },
                    items: [],
                    pagination: {
                        count: 0,
                        current: 0,
                        pages: 0,
                        perPage: 0,
                    },
                    stats: [],
                };

                const recoveryProps: Unbrand<ReportProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof Report>(
                    new Report(recoveryProps as ReportProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new Report(validatedProps));
    }

    toCreateProps(): ReportInputProps {
        return {
            stationReportSummary: this.stationReportSummary,
            items: this.items,
            pagination: this.props.pagination,
            stats: this.props.stats,
        };
    }
}

const ReportsSchema = z
    .object({
        reports: z.array(
            z.custom<Report>((val) => val instanceof Report, {
                error: "Invalid Report instance",
            }),
        ),
        count: z.number().min(0),
    })
    .brand<"Reports">();

export type ReportsInputProps = z.input<typeof ReportsSchema>;
export type ReportsProps = z.output<typeof ReportsSchema>;

export class Reports {
    private constructor(private readonly props: Readonly<ReportsProps>) {}

    get reports(): readonly Readonly<Report>[] {
        return this.props.reports;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): Reports {
        return Reports.create({
            reports: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<ReportsInputProps>>,
    ): Result<ReportsProps, z.ZodError<ReportsProps>> {
        const result = ReportsSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<ReportsInputProps>,
    ): Result<Reports, z.ZodError<ReportsProps>> {
        return Reports.validate(props).map(
            (validatedProps) => new Reports(validatedProps),
        );
    }
}

export interface ReportHistoricalFilter {
    from: number;
    to: number;
    stationIds: number[];
    variableIds?: number[];
    road?: string;
    fromPk?: number;
    toPk?: number;
    originPoi?: string;
    maxValues?: boolean;
    minValues?: boolean;
    avgValues?: boolean;
    sumValues?: boolean;
    measures?: boolean;
    totalSum?: boolean;
    tendency?: boolean;
}

export interface ReportHistoricalCustomFilter {
    from: number;
    to: number;
    fromPk?: number;
    toPk?: number;
    stationId: number;
    variableIds: number[];
    page: number;
    maxValues: boolean;
    minValues: boolean;
    avgValues: boolean;
    sumValues: boolean;
    measures: boolean;
    totalSum: boolean;
    tendency: boolean;
    road?: string;
}
